import { z } from 'zod';
import { IsoDateTimeSchema } from './common/iso-date.js';
import { PaymentProcessorSchema } from './payment.js';
// A payment incident: money that reached (or may have reached) an operator
// without the booking, bono or rental it paid for, or money that was meant to
// go back and did not. Nuberu's rule is that a customer is never left charged
// with nothing to show for it, so every such case is recorded here, alerted,
// and stays open until someone resolves it from the backoffice.
// What was being bought.
export const PAYMENT_INCIDENT_SUBJECTS = ['booking', 'bono', 'rental'];
export const PaymentIncidentSubjectSchema = z.enum(PAYMENT_INCIDENT_SUBJECTS);
// Why it was opened.
//   paid_not_fulfilled — the gateway says the payment succeeded and the
//     booking/bono/rental could not be created, by the webhook or by recovery.
//   returned_ok_not_fulfilled — the customer came back from the bank's page
//     with "OK" and nothing was created (Redsys, where we cannot ask the bank).
//   refund_failed — an automatic refund gave up: money meant to go back did not.
export const PAYMENT_INCIDENT_REASONS = [
    'paid_not_fulfilled',
    'returned_ok_not_fulfilled',
    'refund_failed',
];
export const PaymentIncidentReasonSchema = z.enum(PAYMENT_INCIDENT_REASONS);
// How it was closed.
//   fulfilled — the booking/bono/rental now exists (recovered or created by hand);
//   refunded — the money went back;
//   manual — closed by an admin with a note (handled outside Nuberu).
export const PAYMENT_INCIDENT_RESOLUTIONS = ['fulfilled', 'refunded', 'manual'];
export const PaymentIncidentResolutionSchema = z.enum(PAYMENT_INCIDENT_RESOLUTIONS);
export const PaymentIncidentSchema = z.object({
    id: z.string().uuid(),
    operatorId: z.string().uuid(),
    operatorName: z.string(),
    subject: PaymentIncidentSubjectSchema,
    reason: PaymentIncidentReasonSchema,
    processor: PaymentProcessorSchema,
    paymentIntentId: z.string(),
    // The staging row (booking_intents / bono_purchases / rental_booking_intents).
    stagingId: z.string().uuid(),
    amountCents: z.number().int().nonnegative(),
    currency: z.string(),
    customerName: z.string(),
    customerEmail: z.string(),
    customerPhone: z.string().nullable(),
    // A short machine-readable cause (an error code), never a secret.
    detail: z.string().nullable(),
    detectedAt: IsoDateTimeSchema,
    alertCount: z.number().int().nonnegative(),
    resolvedAt: IsoDateTimeSchema.nullable(),
    resolution: PaymentIncidentResolutionSchema.nullable(),
    resolutionNote: z.string().nullable(),
    resolvedBy: z.string().nullable(),
});
// The backoffice "Pagos sin reserva" tab: every open incident (oldest first)
// and the ones resolved in the last 14 days (newest first).
export const PaymentIncidentListResponseSchema = z.object({
    open: z.array(PaymentIncidentSchema),
    resolved: z.array(PaymentIncidentSchema),
});
// What a backoffice action did.
//   fulfilled — the booking/bono/rental now exists;
//   refunded — the money went back (or, creating it, the place was gone and
//     the payment is being refunded automatically);
//   resolved — closed by hand with a note.
export const PAYMENT_INCIDENT_ACTION_OUTCOMES = ['fulfilled', 'refunded', 'resolved'];
export const PaymentIncidentActionResponseSchema = z.object({
    outcome: z.enum(PAYMENT_INCIDENT_ACTION_OUTCOMES),
    incident: PaymentIncidentSchema,
});
// "Marcar resuelto": what was done, for the record.
export const ResolvePaymentIncidentBodySchema = z.object({
    note: z.string().trim().min(3).max(500),
});
//# sourceMappingURL=payment-incidents.js.map